import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Share2, LayoutDashboard, ImageIcon, Sparkles, Target, Calendar, Package, Globe2, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Skeleton } from '../ui/skeleton';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { useCampaignApi } from '../../services/apiService';
import { type Campaign } from './types';
import { formatBudget, formatDate, getCampaignStatus } from './utils';
import { toast } from 'sonner';

export type CampaignShowcaseLayout = 'spotlight' | 'storyboard' | 'minimal';

interface CampaignShowcasePageProps {
    campaignId: string | null;
    layout: CampaignShowcaseLayout;
    onLayoutChange: (layout: CampaignShowcaseLayout) => void;
    onReturnToDashboard: () => void;
}

interface ShowcaseMetric {
    label: string;
    value: string;
    helper?: string;
    icon: ReactNode;
}

const layoutOptions: { value: CampaignShowcaseLayout; label: string; description: string }[] = [
    { value: 'spotlight', label: 'Spotlight', description: 'Immersive hero layout ideal for storytelling' },
    { value: 'storyboard', label: 'Storyboard', description: 'Split view with narrative sections' },
    { value: 'minimal', label: 'Minimal', description: 'Compact summary for quick reviews' }
];

function buildShareUrl(campaignId: string, layout: CampaignShowcaseLayout) {
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'campaign-showcase');
    url.searchParams.set('campaignId', campaignId);
    url.searchParams.set('layout', layout);
    return url.toString();
}

function getCampaignDuration(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = Math.max(0, end.getTime() - start.getTime());
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return `${days} day${days === 1 ? '' : 's'}`;
}

export function CampaignShowcasePage({ campaignId, layout, onLayoutChange, onReturnToDashboard }: CampaignShowcasePageProps) {
    const campaignApi = useCampaignApi();
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!campaignId) {
            setCampaign(null);
            return;
        }

        let isCancelled = false;
        async function loadCampaign() {
            setIsLoading(true);
            setError(null);
            try {
                const data = await campaignApi.getCampaign(Number(campaignId));
                if (!isCancelled) {
                    setCampaign({
                        id: Number(data.id),
                        name: data.name,
                        businessUnit: data.businessUnit,
                        category: data.category,
                        brandId: Number(data.brandId),
                        brandName: data.brandName || '',
                        concept: data.concept,
                        status: getCampaignStatus(data.startDate, data.endDate),
                        startDate: data.startDate,
                        endDate: data.endDate,
                        year: new Date(data.startDate).getFullYear(),
                        budget: data.budget,
                        targetProfile: data.targetProfile,
                        campaignType: data.campaignType,
                        platforms: data.platforms || [],
                        products: data.products || [],
                        description: data.description,
                        image: data.imageUrl || data.image || '',
                        createdAt: data.createdAt,
                        updatedAt: data.updatedAt
                    });
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error('Failed to load campaign', err);
                    setError('Unable to load campaign details. Please check the link or try again later.');
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        }

        void loadCampaign();

        return () => {
            isCancelled = true;
        };
    }, [campaignApi, campaignId]);

    const metrics: ShowcaseMetric[] = useMemo(() => {
        if (!campaign) {
            return [];
        }

        return [
            {
                label: 'Campaign Type',
                value: campaign.campaignType || '—',
                helper: campaign.concept ? `Concept: ${campaign.concept}` : undefined,
                icon: <Sparkles className="h-4 w-4" />
            },
            {
                label: 'Status',
                value: campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1),
                helper: `${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`,
                icon: <CheckCircle2 className="h-4 w-4" />
            },
            {
                label: 'Budget',
                value: campaign.budget ? formatBudget(campaign.budget) : 'Not set',
                helper: campaign.products?.length ? `${campaign.products.length} product${campaign.products.length === 1 ? '' : 's'}` : undefined,
                icon: <LayoutDashboard className="h-4 w-4" />
            },
            {
                label: 'Duration',
                value: getCampaignDuration(campaign.startDate, campaign.endDate),
                helper: campaign.platforms?.length ? `${campaign.platforms.length} platforms` : undefined,
                icon: <Clock className="h-4 w-4" />
            }
        ];
    }, [campaign]);

    const scorecard = useMemo(() => {
        if (!campaign) return [];
        return [
            {
                label: 'Primary Audience',
                value: campaign.targetProfile || 'TBD',
                icon: <Target className="h-5 w-5 text-amber-600" />
            },
            {
                label: 'Focus Markets',
                value: campaign.businessUnit,
                icon: <Globe2 className="h-5 w-5 text-blue-600" />
            },
            {
                label: 'Go-Live',
                value: formatDate(campaign.startDate),
                icon: <Calendar className="h-5 w-5 text-emerald-600" />
            }
        ];
    }, [campaign]);

    const handleCopyShareLink = async () => {
        if (!campaignId) return;
        const shareUrl = buildShareUrl(campaignId, layout);
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Shareable link copied');
        } catch (copyError) {
            console.error('Unable to copy link', copyError);
            toast.error('Unable to copy link. Please copy it manually.', {
                description: shareUrl
            });
        }
    };

    const renderSpotlightLayout = () => {
        if (!campaign) return null;
        return (
            <div className="space-y-6">
                <div className="relative overflow-hidden rounded-3xl border border-[#e5dccf] bg-black text-white shadow-xl">
                    {campaign.image ? (
                        <ImageWithFallback
                            src={campaign.image}
                            alt={campaign.name}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-black">
                            <ImageIcon className="h-20 w-20 text-white/40" />
                        </div>
                    )}
                    <div className="relative z-10 bg-gradient-to-t from-black/80 via-black/60 to-black/20">
                        <div className="flex flex-col gap-8 p-10 md:p-16 lg:flex-row lg:items-end lg:justify-between">
                            <div className="max-w-2xl space-y-6">
                                <div className="inline-flex items-center gap-3">
                                    <Badge variant="secondary" className="bg-white/20 text-white backdrop-blur">
                                        {campaign.status}
                                    </Badge>
                                    <span className="text-sm text-white/80">{campaign.businessUnit} · {campaign.category}</span>
                                </div>
                                <div className="space-y-4">
                                    <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">{campaign.name}</h1>
                                    {campaign.description && (
                                        <p className="max-w-2xl text-lg text-white/80">
                                            {campaign.description}
                                        </p>
                                    )}
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {metrics.map((metric) => (
                                        <div key={metric.label} className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                                            <div className="flex items-center gap-3 text-sm uppercase tracking-wide text-white/70">
                                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                                                    {metric.icon}
                                                </span>
                                                {metric.label}
                                            </div>
                                            <p className="mt-2 text-xl font-semibold">{metric.value}</p>
                                            {metric.helper && <p className="text-sm text-white/70">{metric.helper}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="w-full max-w-xs space-y-4 rounded-2xl border border-white/10 bg-white/10 p-6 backdrop-blur">
                                <h2 className="text-sm font-semibold uppercase tracking-wide text-white/70">Scorecard</h2>
                                <div className="space-y-4">
                                    {scorecard.map((item) => (
                                        <div key={item.label} className="flex items-start gap-3">
                                            <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                                                {item.icon}
                                            </div>
                                            <div>
                                                <p className="text-xs uppercase tracking-wide text-white/60">{item.label}</p>
                                                <p className="text-base font-semibold text-white">{item.value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {campaign.products?.length ? (
                    <Card className="border-[#e5dccf]">
                        <CardHeader>
                            <CardTitle>Featured Products</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {campaign.products.map((product) => (
                                    <Badge key={product} variant="secondary" className="bg-amber-50 text-amber-700">
                                        {product}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ) : null}
            </div>
        );
    };

    const renderStoryboardLayout = () => {
        if (!campaign) return null;
        return (
            <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
                <div className="space-y-6">
                    <Card className="overflow-hidden border-[#e5dccf]">
                        {campaign.image ? (
                            <ImageWithFallback
                                src={campaign.image}
                                alt={campaign.name}
                                className="h-72 w-full object-cover"
                            />
                        ) : (
                            <div className="flex h-72 w-full items-center justify-center bg-gradient-to-br from-stone-200 to-stone-300">
                                <ImageIcon className="h-12 w-12 text-stone-500" />
                            </div>
                        )}
                        <CardHeader className="space-y-3">
                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                <Badge variant="secondary">{campaign.status}</Badge>
                                <span>{campaign.businessUnit}</span>
                                <span>•</span>
                                <span>{campaign.category}</span>
                            </div>
                            <div>
                                <CardTitle className="text-3xl font-semibold text-gray-900">{campaign.name}</CardTitle>
                                <p className="mt-2 text-base text-gray-600">{campaign.description || 'No description provided yet.'}</p>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Mission</h3>
                                <p className="text-gray-700">
                                    {campaign.concept ? `Built on the “${campaign.concept}” concept to deliver a ${campaign.campaignType?.toLowerCase() || 'marketing'} impact.` : 'Concept to be finalised.'}
                                </p>
                            </section>

                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Timeline</h3>
                                <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1">
                                        <Clock className="h-4 w-4 text-amber-600" />
                                        <span>{formatDate(campaign.startDate)} → {formatDate(campaign.endDate)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1">
                                        <Package className="h-4 w-4 text-blue-600" />
                                        <span>{campaign.products?.length || 0} deliverables</span>
                                    </div>
                                </div>
                            </section>
                        </CardContent>
                    </Card>

                    <Card className="border-[#e5dccf]">
                        <CardHeader>
                            <CardTitle>Activation Channels</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {campaign.platforms?.length ? (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {campaign.platforms.map((platform) => (
                                        <div key={platform} className="flex items-center gap-3 rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-4">
                                            <Sparkles className="h-5 w-5 text-amber-600" />
                                            <span className="font-medium text-gray-800">{platform}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">Channels will be defined once the media plan is ready.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card className="border-[#e5dccf]">
                        <CardHeader>
                            <CardTitle>Essential Metrics</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {metrics.map((metric) => (
                                <div key={metric.label} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                                    <div className="flex items-center gap-3 text-sm font-semibold text-gray-600">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
                                            {metric.icon}
                                        </span>
                                        {metric.label}
                                    </div>
                                    <p className="mt-3 text-xl font-semibold text-gray-900">{metric.value}</p>
                                    {metric.helper && <p className="text-sm text-gray-500">{metric.helper}</p>}
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Card className="border-[#e5dccf]">
                        <CardHeader>
                            <CardTitle>Scorecard Highlights</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {scorecard.map((item) => (
                                <div key={item.label} className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 p-4">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-50">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wide text-gray-500">{item.label}</p>
                                        <p className="text-base font-semibold text-gray-900">{item.value}</p>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    };

    const renderMinimalLayout = () => {
        if (!campaign) return null;
        return (
            <div className="space-y-6">
                <Card className="border-[#e5dccf]">
                    <CardContent className="grid gap-6 p-6 md:grid-cols-[1fr,1.2fr] md:items-center">
                        <div className="space-y-4">
                            <Badge variant="secondary" className="w-fit uppercase tracking-wide">
                                {campaign.status}
                            </Badge>
                            <div className="space-y-2">
                                <h1 className="text-3xl font-semibold text-gray-900">{campaign.name}</h1>
                                <p className="text-gray-600">{campaign.description || 'Campaign summary will be added soon.'}</p>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {metrics.map((metric) => (
                                    <div key={metric.label} className="rounded-xl border border-gray-200 p-3">
                                        <p className="text-xs uppercase tracking-wide text-gray-500">{metric.label}</p>
                                        <p className="mt-1 text-lg font-semibold text-gray-900">{metric.value}</p>
                                        {metric.helper && <p className="text-xs text-gray-500">{metric.helper}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative overflow-hidden rounded-2xl border border-dashed border-gray-200 bg-gray-50">
                            {campaign.image ? (
                                <ImageWithFallback src={campaign.image} alt={campaign.name} className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full min-h-[260px] items-center justify-center text-gray-400">
                                    <ImageIcon className="h-12 w-12" />
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-[#e5dccf]">
                    <CardHeader>
                        <CardTitle>Quick Facts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <Fact label="Business Unit" value={campaign.businessUnit} />
                            <Fact label="Category" value={campaign.category} />
                            <Fact label="Products" value={campaign.products?.join(', ') || 'To be confirmed'} />
                            <Fact label="Platforms" value={campaign.platforms?.join(', ') || 'Planning in progress'} />
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="space-y-4">
                    <Skeleton className="h-96 w-full rounded-3xl" />
                    <Skeleton className="h-48 w-full rounded-2xl" />
                </div>
            );
        }

        if (error) {
            return (
                <Card className="border-red-200 bg-red-50/60">
                    <CardContent className="flex items-start gap-3 p-6">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <div>
                            <p className="font-medium text-red-700">{error}</p>
                            <Button variant="link" className="mt-2 p-0" onClick={onReturnToDashboard}>
                                Return to campaign dashboard
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (!campaign) {
            return (
                <Card className="border-amber-200 bg-amber-50/70">
                    <CardContent className="flex items-start justify-between gap-3 p-6">
                        <div>
                            <p className="font-semibold text-amber-800">Select a campaign to view its showcase page.</p>
                            <p className="text-sm text-amber-700">You can pick a campaign from the dashboard list.</p>
                        </div>
                        <Button onClick={onReturnToDashboard} variant="secondary">
                            Back to dashboard
                        </Button>
                    </CardContent>
                </Card>
            );
        }

        switch (layout) {
            case 'spotlight':
                return renderSpotlightLayout();
            case 'storyboard':
                return renderStoryboardLayout();
            case 'minimal':
            default:
                return renderMinimalLayout();
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-4 rounded-3xl border border-[#e5dccf] bg-[#f8f5f0] p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">Campaign Showcase</p>
                    <h2 className="text-2xl font-semibold text-gray-900">Share-ready campaign narrative</h2>
                    <p className="text-sm text-gray-600">Choose a layout, preview the story, and share a read-only link with stakeholders.</p>
                </div>
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                    <ToggleGroup type="single" value={layout} onValueChange={(value) => value && onLayoutChange(value as CampaignShowcaseLayout)}>
                        {layoutOptions.map((option) => (
                            <ToggleGroupItem key={option.value} value={option.value} className="data-[state=on]:bg-amber-100 data-[state=on]:text-amber-800">
                                <div className="flex flex-col text-left">
                                    <span className="text-sm font-semibold">{option.label}</span>
                                    <span className="text-xs text-muted-foreground">{option.description}</span>
                                </div>
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                    <Button variant="outline" onClick={handleCopyShareLink} disabled={!campaignId} className="border-amber-300 text-amber-700 hover:bg-amber-100">
                        <Share2 className="mr-2 h-4 w-4" />
                        Copy share link
                    </Button>
                </div>
            </header>

            {renderContent()}
        </div>
    );
}

interface FactProps {
    label: string;
    value: string;
}

function Fact({ label, value }: FactProps) {
    return (
        <div className="space-y-1 rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
            <p className="text-sm font-semibold text-gray-900">{value}</p>
        </div>
    );
}

